import {
  DetailsList,
  DetailsListLayoutMode,
  IColumn,
  MessageBar,
  MessageBarType,
  ProgressIndicator,
  SearchBox,
  SelectionMode,
  Stack,
} from '@fluentui/react';
import dateFormat from 'dateformat';
import { useEffect, useMemo, useState } from 'react';

import { formsSearch } from '../../../utils/search';
import FormActions from '../List/FormActions';
import FormList from '../List/FormList';

type Props = {
  actions?: (formId: string) => JSX.Element;
  useLatestOnIdle?: boolean;
  compact?: boolean;
};

type Item = {
  id: string;
  displayName: string;
  phones: string[];
  lastUpdated: number;
};

export default (props: Props) => {
  const [query, setQuery] = useState<string>();
  const [isLoading, setIsLoading] = useState(false);
  const [items, setItems] = useState<Item[]>();

  const columns: IColumn[] = [
    {
      key: "id",
      minWidth: 16,
      flexGrow: 0.5,
      name: "Evrak No.",
      data: { hideOnCompact: true },
      onRender: (item?: Item) => item?.id,
    },
    {
      key: "description",
      minWidth: 16,
      flexGrow: 1,
      name: "Açıklama",
      onRender: (item?: Item) => item?.displayName,
    },
    {
      key: "phones",
      minWidth: 16,
      flexGrow: 2,
      name: "Telefonlar",
      data: { hideOnCompact: true },
      onRender: (item?: Item) => (
        <Stack horizontal tokens={{ childrenGap: 20 }}>
          {item?.phones?.map((phone, index) => (
            <span key={index}>{phone}</span>
          ))}
        </Stack>
      ),
    },
    {
      key: "created",
      minWidth: 16,
      flexGrow: 1,
      name: "Son Düzenleme",
      data: { showOnCompact: true },
      onRender: (item?: Item) =>
        item?.lastUpdated &&
        dateFormat(new Date(item.lastUpdated), "dd/mm/yyyy HH:MM"),
    },
    {
      key: "actions",
      minWidth: 16,
      flexGrow: 1,
      name: "Eylemler",
      onRender: (item?: Item) =>
        item && (props.actions?.(item.id) || <FormActions formId={item.id} />),
    },
  ]
    .filter((c) => !c.data?.hideOnCompact || !props.compact)
    .filter((c) => !c.data?.showOnCompact || props.compact);

  useEffect(() => {
    const hash = Date.now();
    searchHash = hash;

    if (items?.length !== 0) setItems(undefined);

    if (!query) return;

    if (!isLoading) {
      setIsLoading(true);
    }

    formsSearch.search(query).then(({ hits }) => {
      if (searchHash !== hash) return;

      setItems(
        hits.map(
          (hit: any) =>
            ({
              id: hit.objectID,
              displayName: hit.displayName,
              phones: hit.phones,
              lastUpdated: hit.lastUpdated,
            } as Item)
        )
      );
      setIsLoading(false);
    });
  }, [query]);

  return (
    <Stack tokens={{ childrenGap: 5 }}>
      <SearchBox
        lang="tr"
        placeholder="Müşteri adı veya telefon numarası..."
        onChange={(_, nv) =>
          (nv?.length || 0) >= 3 ? setQuery(nv) : setQuery(undefined)
        }
      />
      {isLoading ? (
        <ProgressIndicator />
      ) : items?.length === 0 ? (
        <MessageBar messageBarType={MessageBarType.warning}>
          Aramaya uygun form bulunamadı
        </MessageBar>
      ) : items ? (
        <DetailsList
          items={items}
          columns={columns}
          selectionMode={SelectionMode.none}
          layoutMode={DetailsListLayoutMode.fixedColumns}
          compact
        />
      ) : props.useLatestOnIdle ? (
        <FormList actions={props.actions} compact={props.compact} />
      ) : undefined}
    </Stack>
  );
};

let searchHash = 0;
